import { useEffect, useState } from 'react';
import { Package, ShoppingBag, AlertCircle, Globe, Lock } from 'lucide-react';
import { vendorApi } from '../../api/vendorApi';
import { apiClient } from '@/lib/apiClient';

// تبدیل اعداد به فارسی
const toPersianDigits = (str) => {
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return str.toString().replace(/\d/g, (d) => persianDigits[d]).replace(/\./g, '٫');
};

export default function VendorDashboard() {
  const [stats, setStats] = useState({
    productCount: 0,
    orderCount: 0,
    publicPackages: 0,
    privatePackages: 0
  });
  const [state, setState] = useState('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      // دریافت همزمان محصولات، سفارشات و پکیج‌ها
      const [productsResult, ordersResult, packagesResult] = await Promise.allSettled([
        vendorApi.getProducts(),
        vendorApi.getOrders({ page: 1, size: 1 }),
        apiClient.GET('/api/v1/vendor/packages') // فرض بر این است که این اندپوینت لیست پکیج‌ها را برمی‌گرداند
      ]);

      const products = productsResult.status === 'fulfilled' ? productsResult.value : null;
      const orders = ordersResult.status === 'fulfilled' ? ordersResult.value : null;

      let publicPkgs = 0;
      let privatePkgs = 0;

      // اگر لیست پکیج‌ها با موفقیت دریافت شد، آن‌ها را بر اساس عمومی/اختصاصی تفکیک می‌کنیم
      if (packagesResult.status === 'fulfilled' && packagesResult.value?.data) {
        const packages = packagesResult.value.data;
        publicPkgs = packages.filter(p => !p.is_private).length;
        privatePkgs = packages.filter(p => p.is_private).length;
      }

      if (productsResult.status === 'fulfilled' || ordersResult.status === 'fulfilled' || packagesResult.status === 'fulfilled') {
        setStats({
          productCount: products?.length ?? 0,
          orderCount: orders?.total ?? 0,
          publicPackages: publicPkgs,
          privatePackages: privatePkgs,
        });
        setState('ready');

        if (productsResult.status === 'rejected' || ordersResult.status === 'rejected' || packagesResult.status === 'rejected') {
          console.error('Vendor dashboard request partially failed', {
            products: productsResult.reason,
            orders: ordersResult.reason,
            packages: packagesResult.reason,
          });
          setError('بخشی از اطلاعات داشبورد دریافت نشد');
        }
      } else {
        console.error('Vendor dashboard requests failed');
        setError('خطا در دریافت اطلاعات داشبورد');
        setState('error');
      }
    })();
  }, []);

  if (state === 'error') {
    return <div className="admin-table-error"><AlertCircle size={16} />{error}</div>;
  }

  // === استایل‌های مشترک برای تمام کارت‌ها (مشابه داشبورد پنل شرکت) ===
  const cardStyle = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1.5rem 1rem',
    minHeight: '110px',
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    border: '1px solid #e2e8f0',
  };

  const iconWrapperStyle = (bgColor) => ({
    position: 'absolute',
    right: '1.5rem',
    top: '50%',
    transform: 'translateY(-50%)',
    background: bgColor,
    width: '55px',
    height: '55px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  const contentStyle = {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    paddingRight: '4.5rem',
    paddingLeft: '1rem',
  };

  const titleStyle = {
    margin: 0,
    marginBottom: '0.5rem',
    color: '#64748b',
    fontWeight: 'normal',
    fontSize: '0.9rem',
    whiteSpace: 'nowrap'
  };

  return (
      <div style={{ direction: 'rtl', display: 'flex', flexDirection: 'column', gap: '2rem', marginTop: 0, paddingTop: 0 }}>

        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <h1 style={{ fontSize: '1.5rem', color: '#1e293b', margin: 0 }}>داشبورد فروشنده</h1>
        </header>

        {error && <div className="admin-table-error" style={{ marginBottom: '1rem' }}><AlertCircle size={16} />{error}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.5rem' }}>

          {/* کارت ۱: تعداد محصولات */}
          <div className="stat-card" style={cardStyle}>
            <div style={iconWrapperStyle('#eff6ff')}>
              <Package size={24} color="#2563eb" />
            </div>
            <div style={contentStyle}>
              <h4 style={titleStyle}>تعداد محصولات</h4>
              <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: 'bold', color: '#1e293b' }}>
                {state === 'loading' ? '—' : toPersianDigits(stats.productCount)} عدد
              </p>
            </div>
          </div>

          {/* کارت ۲: تعداد سفارشات */}
          <div className="stat-card" style={cardStyle}>
            <div style={iconWrapperStyle('#ecfdf5')}>
              <ShoppingBag size={24} color="#10b981" />
            </div>
            <div style={contentStyle}>
              <h4 style={titleStyle}>تعداد سفارشات</h4>
              <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: 'bold', color: '#1e293b' }}>
                {state === 'loading' ? '—' : toPersianDigits(stats.orderCount)} سفارش
              </p>
            </div>
          </div>

          {/* کارت ۳: پکیج‌های عمومی */}
          <div className="stat-card" style={cardStyle}>
            <div style={iconWrapperStyle('#fffbeb')}>
              <Globe size={24} color="#f59e0b" />
            </div>
            <div style={contentStyle}>
              <h4 style={titleStyle}>پکیج‌های عمومی</h4>
              <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: 'bold', color: '#1e293b' }}>
                {state === 'loading' ? '—' : toPersianDigits(stats.publicPackages)} پکیج
              </p>
            </div>
          </div>

          {/* کارت ۴: پکیج‌های اختصاصی */}
          <div className="stat-card" style={cardStyle}>
            <div style={iconWrapperStyle('#f5f3ff')}>
              <Lock size={24} color="#8b5cf6" />
            </div>
            <div style={contentStyle}>
              <h4 style={titleStyle}>پکیج‌های اختصاصی</h4>
              <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: 'bold', color: '#1e293b' }}>
                {state === 'loading' ? '—' : toPersianDigits(stats.privatePackages)} پکیج
              </p>
            </div>
          </div>

        </div>
      </div>
  );
}